/**
 * DimInput — SketchUp-style dynamic dimension box.
 *
 * While a gesture is in progress it floats near the cursor as a live
 * readout ("64.0 × 32.5"). The moment the user types, it becomes an input;
 * Enter parses the numbers ("40x20", "35", "24,12") and commits exact
 * values, ending the gesture. Escape returns to readout mode.
 */

export class DimInput {
  private el: HTMLDivElement;
  private input: HTMLInputElement | null = null;
  private label = '';
  private onCommit: ((values: number[], raw: string) => void) | null = null;
  /** When true, Enter commits digit-free input too (expressions like "w/2"). */
  acceptText = false;

  constructor(private readonly host: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'nv-dim';
    this.el.style.display = 'none';
    host.appendChild(this.el);
  }

  get isEditing(): boolean {
    return this.input !== null;
  }

  /** Show/refresh the readout. No-op for position while editing. */
  show(
    text: string,
    clientX: number,
    clientY: number,
    onCommit: ((v: number[], raw: string) => void) | null,
  ): void {
    this.label = text;
    this.onCommit = onCommit;
    if (this.input) return; // user is typing — don't fight them
    const rect = this.host.getBoundingClientRect();
    this.el.style.display = 'block';
    this.el.style.left = `${clientX - rect.left + 16}px`;
    this.el.style.top = `${clientY - rect.top + 18}px`;
    this.el.textContent = text;
  }

  /** Switch to typing mode, seeded with the first typed character. */
  beginEdit(firstChar: string): void {
    if (this.input || this.el.style.display === 'none' || !this.onCommit) return;
    this.el.textContent = '';
    this.input = document.createElement('input');
    this.input.value = firstChar;
    this.el.appendChild(this.input);
    this.input.focus();
    this.input.setSelectionRange(1, 1);
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        const raw = this.input!.value;
        const values = (raw.match(/-?\d*\.?\d+/g) ?? []).map(Number);
        const commit = this.onCommit;
        const textOk = this.acceptText && raw.trim().length > 0;
        this.hide();
        if (values.length > 0 || textOk) commit?.(values, raw);
      } else if (e.key === 'Escape') {
        this.endEdit();
      }
    });
  }

  private endEdit(): void {
    this.input?.remove();
    this.input = null;
    this.el.textContent = this.label;
  }

  hide(): void {
    this.input?.remove();
    this.input = null;
    this.onCommit = null;
    this.acceptText = false;
    this.el.style.display = 'none';
  }
}
